import { HelpText } from '@ovComponents/2-component/helpText/helpText';
import { useHelpTextTokens } from '@ovComponents/2-component/helpText/helpText.tokens';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import type { SwitchToken, Theme } from 'hephaestus/core';
import { Box, Stack, Switch, Typography, useTheme } from 'hephaestus/web';
import { merge } from 'lodash-es';
import { type ReactElement, useEffect, useState } from 'react';

type SwitchFieldProps = {
  checked?: boolean;
  onChange?: (onoff: boolean) => void;
  disabled?: boolean;
  error?: boolean;
  label?: string;
  labelPosition?: 'right' | 'left';
  size?: 'medium' | 'small';
  infoTooltip?: string;
  fullWidth?: boolean;
  sx?: any;
  'data-testid'?: string;
};

const useLabelTokens = (
  theme: Theme,
): Pick<SwitchToken, 'labelColor' | 'errorColor'> => {
  const { color, component } = theme;
  return merge(
    {},
    {
      labelColor: color.onSurfaceVariant,
      errorColor: color.negative,
    },
    {
      labelColor: component?.switch?.labelColor,
      errorColor: component?.switch?.errorColor,
    },
  );
};

/**
 * Labeled switch field composed on Hephaestus `Switch`.
 * Prefer bare `Switch` from `hephaestus/web` when no label/tooltip is needed.
 */
export const SwitchField = ({
  checked,
  onChange,
  disabled = false,
  error,
  label,
  labelPosition = 'right',
  size = 'medium',
  infoTooltip,
  fullWidth = false,
  sx,
  'data-testid': dataTestId = 'switch',
}: SwitchFieldProps): ReactElement => {
  const [on, setOn] = useState<boolean>(checked ?? false);
  const theme = useTheme();
  const helpTextTokens = useHelpTextTokens(theme);
  const tokens = useLabelTokens(theme);

  useEffect(() => {
    if (checked !== undefined) setOn(checked);
  }, [checked]);

  const setOnAndPropagate = (newOn: boolean) => {
    setOn(newOn);
    onChange?.(newOn);
  };

  const labelNode = label ? (
    <Box
      onClick={disabled ? undefined : () => setOnAndPropagate(!on)}
      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
    >
      <Typography
        variant={size === 'small' ? 'bodySmall' : 'bodyLarge'}
        sx={{
          color: error ? tokens.errorColor : tokens.labelColor,
          mr: '6px',
          cursor: disabled ? 'default' : 'pointer',
        }}
        data-testid={`${dataTestId}-label`}
      >
        {label}
        {infoTooltip && helpTextTokens.displayInDialog && (
          <InfoDialog information={infoTooltip} />
        )}
      </Typography>
    </Box>
  ) : null;

  return (
    <Box>
      <Stack
        direction='row'
        spacing={1.5}
        sx={[
          {
            alignItems: 'center',
            justifyContent: fullWidth ? 'space-between' : undefined,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {labelPosition === 'left' && labelNode}
        <Switch
          slotProps={{
            root: { 'data-testid': dataTestId },
            input: { 'data-testid': `${dataTestId}-input` },
          }}
          checked={on}
          disabled={disabled}
          size={size}
          onChange={(event) => {
            setOnAndPropagate(event.target.checked);
          }}
        />
        {labelPosition === 'right' && labelNode}
      </Stack>
      {infoTooltip && !helpTextTokens.displayInDialog && (
        <HelpText text={infoTooltip} tone='neutral' />
      )}
    </Box>
  );
};
